/**
 * Rotation Breakdown Page
 * 
 * Displays complete rotation details with:
 * - Flight timeline with duty periods, legs, layovers
 * - Pay breakdown tab (rig comparison, line items, per-duty summaries)
 * - FAR 117 compliance sidebar
 * 
 * Architecture:
 * - URL: /rotation-breakdown?id={rotation_number}
 * - Uses rotation_number (e.g., "4302") + current user's employee_id
 * - Desktop-first layout, mobile responsive
 * - State-based tab navigation (no URL routing for tabs)
 * - Blocks uncompiled rotations with "Compile Now" button
 * - Requires user_settings to be configured
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import axios from 'axios';

// Components
import Sidebar from '../../components/ui/Sidebar';
import Header from './components/Header';
import QuickStats from './components/QuickStats';
import TabNavigation from './components/TabNavigation';
import FlightTimeline from './components/FlightTimeline';
import PayBreakdownTab from './components/PayBreakdownTab';
import ComplianceSidebar from './components/ComplianceSidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorDisplay from '../../components/ErrorDisplay';
import CompileNowButton from './components/CompileNowButton';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const RotationBreakdownPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userSettings } = useAuth();
  
  // URL params
  const rotationNumber = searchParams.get('id'); // e.g., "4302"
  
  // Data state
  const [compiledData, setCompiledData] = useState(null);
  const [payData, setPayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('flights'); // 'flights' | 'pay'
  
  // Validation: Check if rotation number provided
  useEffect(() => {
    if (!rotationNumber) {
      setError('No rotation number provided');
      setLoading(false);
    }
  }, [rotationNumber]);
  
  // TODO: Re-enable settings validation after dev mode improvements
  // useEffect(() => {
  //   if (user && !userSettings?.employee_id) {
  //     console.warn('No employee_id found, redirecting to settings');
  //     navigate('/settings?setup=required');
  //   }
  // }, [user, userSettings, navigate]);
  
  // Fetch compiled rotation data
  useEffect(() => {
    // In dev mode, use mock employee_id if userSettings not loaded
    const employeeId = userSettings?.employee_id || '141986'; // Mock for dev
    
    if (!rotationNumber) return;
    
    const fetchCompiledRotation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching rotation:', rotationNumber, 'for employee:', employeeId);
        
        // API call with employee_id from auth context (Option A)
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/rotations/${rotationNumber}/compiled`,
          {
            params: {
              employee_id: employeeId
            }
          }
        );
        
        console.log('Compiled data received:', response.data);
        setCompiledData(response.data);
      } catch (err) {
        console.error('Error fetching compiled rotation:', err);
        
        if (err.response?.status === 404) {
          setError(`Rotation ${rotationNumber} not found for your account`);
        } else if (err.response?.data?.detail) {
          setError(err.response.data.detail);
        } else {
          setError('Failed to load rotation data');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompiledRotation();
  }, [rotationNumber]); // Removed userSettings dependency
  
  // Fetch pay data (only when Pay tab is active)
  useEffect(() => {
    if (activeTab !== 'pay' || !rotationNumber) return;
    if (payData) return; // Already loaded
    
    const fetchPayData = async () => {
      try {
        const employeeId = userSettings?.employee_id || '141986'; // Same fallback as compiled rotation
        console.log('Fetching pay data for rotation:', rotationNumber, 'employee:', employeeId);
        
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/pay/calculate/rotation/${rotationNumber}`,
          {
            params: {
              employee_id: employeeId
            }
          }
        );
        
        console.log('Pay data received:', response.data);
        setPayData(response.data);
      } catch (err) {
        console.error('Error fetching pay data:', err);
        // Don't block the page - pay tab will show error
      }
    };
    
    fetchPayData();
  }, [activeTab, rotationNumber, payData]); // Removed userSettings dependency
  
  // Check if rotation is compiled
  const isCompiled = compiledData?.compiled_json != null;
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ErrorDisplay 
          message={error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }
  
  // Uncompiled rotation - show compile button
  if (!isCompiled) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold mb-4">Rotation {rotationNumber}</h1>
          <CompileNowButton 
            rotationNumber={rotationNumber}
            onCompiled={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
  
  const { compiled_json } = compiledData;
  
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      
      <div className="main-content min-h-screen bg-gray-50">
      {/* Header Section */}
      <Header 
        meta={compiled_json.meta}
        rotationNumber={rotationNumber}
      />
      
      {/* Quick Stats Bar */}
      <QuickStats 
        totals={{
          total_duty_minutes: compiledData.total_duty_minutes,
          total_sched_block_minutes: compiledData.total_block_minutes,
          total_actual_block_minutes: compiledData.total_block_minutes,
          total_deadhead_minutes: compiledData.total_deadhead_minutes || 0
        }}
        dutyPeriodCount={compiled_json.duty_periods?.length || 0}
      />
      
      {/* Tab Navigation */}
      <TabNavigation 
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      
      {/* Main Content Area */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Content (70%) */}
          <div className="lg:col-span-8">
            {activeTab === 'flights' && (
              <FlightTimeline 
                dutyPeriods={compiled_json.duty_periods}
                meta={compiled_json.meta}
              />
            )}
            
            {activeTab === 'pay' && (
              <PayBreakdownTab 
                payData={payData}
                userSettings={userSettings}
                rotationNumber={rotationNumber}
              />
            )}
          </div>
          
          {/* Compliance Sidebar (30%) */}
          <div className="lg:col-span-4">
            <ComplianceSidebar 
              far117Compliant={compiled_json.far117_compliant}
              far117Violations={compiled_json.far117_violations || []}
              dutyPeriods={compiled_json.duty_periods}
              totals={{
                total_duty_minutes: compiledData.total_duty_minutes,
                total_block_minutes: compiledData.total_block_minutes
              }}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RotationBreakdownPage;
